import { useApi } from '@/config/apiFetcher'
import { cesiumConfig } from '@/config/cesium.config'
import { useDeployProgressStore } from '@/store/deployProgress'
import { useUserStore } from '@/store/userStore'
import log from '@/utils/useLogger'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import copyToClipboard from '@/utils/copyToClipboard'
import useDroneLocation from '../Drone/useDroneLocation'
import { deviceTypes, dockTypes, rcTypes } from '@/constants/deviceManager'
import useApp from '../useApp'
import { validateDeployPayload, validateMqttHostForDock, type DeployPayload } from './deployPayload'

type DockDeploying = {
  dock_id: string
  status: 'deploying' | 'deployed' | 'failed'
  workflow_run_id?: number
}
const docksDeploying = ref<DockDeploying[]>([])
const devicesInPairing = ref<string[]>([])
const pairingResults = ref<Array<{ deviceId: string; code: string; timestamp: Date }>>([])
const copiedDevices = ref<string[]>([])
const pollingIntervals = ref<Map<string, NodeJS.Timeout>>(new Map())
const workflowRunIds = ref<Map<string, number>>(new Map())

/**
 * FLY3D-1085 — the payload shape is now the deploy contract itself,
 * defined by `src/config/deploy-payload.schema.json`. `mqtt_port` is
 * gone: it was never a workflow input (the workflow hardcodes it) and
 * `additionalProperties: false` now rejects it outright.
 */
export type DeployDockPayload = DeployPayload

/**
 * Composable for device pairing functionality
 * @returns Object with methods for generating pairing code, device config, sim location, and pairing device
 */
export default () => {
  const api = useApi()
  const url = import.meta.env.VITE_DEVICE_PAIRING
  const { organisationId } = storeToRefs(useUserStore())
  const { organizationDevices } = useApp()
  // FLY3D-612 — read live deploy state from the WebSocket-fed store so
  // device-list buttons reflect real progress for any dock_id.
  const deployProgressStore = useDeployProgressStore()
  const { entries: deployEntries } = storeToRefs(deployProgressStore)
  /**
   *
   * @returns A secure 8-digit pairing code avoiding weak patterns
   */
  const generatePairingCode = (): string => {
    const digits: number[] = []
    const buffer = new Uint8Array(16) // Temporary buffer for random bytes
    const threshold = 250 // Acceptable range to avoid modulo bias

    while (digits.length < 8) {
      crypto.getRandomValues(buffer)

      for (const byte of buffer) {
        if (byte < threshold) {
          digits.push(byte % 10)
          if (digits.length === 8) break
        }
      }
    }

    return digits.join('')
  }
  const addDeviceToPairing = (deviceId: string) => {
    if (!devicesInPairing.value.includes(deviceId)) {
      devicesInPairing.value.push(deviceId)
    }
  }

  const removeDeviceFromPairing = (deviceId: string) => {
    const index = devicesInPairing.value.indexOf(deviceId)
    if (index !== -1) {
      devicesInPairing.value.splice(index, 1)
    }
  }

  const isDevicePairing = (deviceId: string) => {
    return devicesInPairing.value.includes(deviceId)
  }

  const addPairingResult = (deviceId: string, code: string) => {
    pairingResults.value.unshift({ deviceId, code, timestamp: new Date() })
    // Keep only the 10 most recent results
    if (pairingResults.value.length > 10) {
      pairingResults.value = pairingResults.value.slice(0, 10)
    }
  }

  const clearPairingResults = () => {
    pairingResults.value = []
  }

  const getPairingCodeForDevice = (deviceId: string): string | null => {
    const result = pairingResults.value.find((r) => r.deviceId === deviceId)
    return result ? result.code : null
  }

  const isDeviceCopied = (deviceId: string): boolean => {
    return copiedDevices.value.includes(deviceId)
  }

  const copyPairingCodeForDevice = async (deviceId: string): Promise<void> => {
    const code = getPairingCodeForDevice(deviceId)
    if (!code) return

    try {
      await copyToClipboard(code)

      // Add to copied devices array
      if (!copiedDevices.value.includes(deviceId)) copiedDevices.value.push(deviceId)

      // Remove from copied array after 2 seconds
      setTimeout(() => {
        const index = copiedDevices.value.indexOf(deviceId)
        if (index !== -1) {
          copiedDevices.value.splice(index, 1)
        }
      }, 2000)
    } catch (error) {
      log.error('Failed to copy pairing code:', error)
    }
  }

  /**
   * Generates device configuration based on device type and name
   * @param deviceName - The name of the device
   * @param deviceType - Type of device: DJI_RC_Plus, DJI_RC_Pro, or IDI_DOCK_S
   * @param customIpAddress - Optional custom IP address to use instead of localhost
   * @param configOptions - Optional overrides for liveStreamEnable and simulatorEnabled
   * @returns Promise with configuration object and generated pairing code
   */
  const generateDeviceConfig = async (
    deviceName: string,
    deviceType: (typeof deviceTypes)[number],
    customIpAddress?: string,
    configOptions?: { liveStreamEnable?: boolean; simulatorEnabled?: boolean },
  ) => {
    const baseConfig = {
      deviceName,
      liveStreamEnable: configOptions?.liveStreamEnable ?? true,
      simulatorEnabled: configOptions?.simulatorEnabled ?? true,
      channelId: deviceName,
      token: '',
      uid: '1234567',
      organisationID: organisationId.value,
    }

    const isDJIRC = rcTypes.includes(deviceType)
    const ipAddress = customIpAddress?.trim() || 'localhost'

    let config
    if (isDJIRC) {
      const location = await getSimlocation()
      config = {
        ...baseConfig,
        headingAngle: 158,
        MQTT_NAME: deviceName,
        BROKER: `tcp://${ipAddress}:1883`,
        simLat: location.latitude,
        simLon: location.longitude,
        smp_server: ipAddress,
        smbPassword: 'test1234',
        smbUsername: 'pi',
        smbAppfolder: 'shared/updates',
        redisEnabled: true,
        redisHost: ipAddress,
        redisPort: 6379,
        redisPassword: '',
        redisDatabase: 0,
        redisConnectionTimeout: 5000,
        redisMaxPoolSize: 10,
        redisMinIdleSize: 2,
        redisTtlMinutes: 60,
        redisKeyPrefix: 'drone:',
        networkLoggerEnabled: true,
        networkLoggerUrl: `smb://${ipAddress}/shared/logs/`,
        networkLoggerProtocol: 'SMB',
        networkLoggerUsername: 'pi',
        networkLoggerPassword: 'test1234',
        networkLoggerBufferSize: 20,
        networkLoggerFlushIntervalMs: 5000,
      }
    } else {
      config = baseConfig
    }

    return {
      config,
      code: generatePairingCode(),
    }
  }

  /**
   * Gets simulation location using user location first, fallback to default
   * @returns Promise with longitude and latitude coordinates
   */
  const getSimlocation = async (): Promise<{ longitude: number; latitude: number }> => {
    const fallbackLocation = {
      longitude: cesiumConfig.defaultDronePosition.longitude,
      latitude: cesiumConfig.defaultDronePosition.latitude,
    }
    try {
      const { fetchUserLocation } = useDroneLocation()
      const droneLocation = await fetchUserLocation()
      if (droneLocation) return droneLocation
      return fallbackLocation
    } catch (error) {
      log.error('Error getting drone location:', error)
      return fallbackLocation
    }
  }

  /**
   * Validates imported JSON config structure against default config
   * @param config - The imported configuration object
   * @param deviceType - Type of device to validate against
   * @returns Promise<boolean> indicating if config is valid
   */
  const validateImportedConfig = async (
    config: any,
    deviceType: (typeof rcTypes)[number],
  ): Promise<boolean> => {
    if (!config || typeof config !== 'object') {
      log.error('Invalid config: must be a valid JSON object')
      return false
    }

    try {
      // Generate the default config for this device type to get required fields
      const defaultConfigData = await generateDeviceConfig('temp-device', deviceType, 'localhost')
      const defaultConfig = defaultConfigData.config

      // Check that all default config fields exist in the imported config
      const missingFields: string[] = []

      const checkFields = (defaultObj: any, importedObj: any, path = '') => {
        for (const key in defaultObj) {
          const currentPath = path ? `${path}.${key}` : key

          if (!(key in importedObj)) {
            missingFields.push(currentPath)
          } else if (
            typeof defaultObj[key] === 'object' &&
            defaultObj[key] !== null &&
            !Array.isArray(defaultObj[key])
          ) {
            // Recursively check nested objects
            checkFields(defaultObj[key], importedObj[key], currentPath)
          }
        }
      }

      checkFields(defaultConfig, config)

      if (missingFields.length > 0) {
        log.warn(`Missing required fields: ${missingFields.join(', ')}`)
        return false
      }

      return true
    } catch (error) {
      log.error('Error validating config:', error)
      return false
    }
  }

  /**
   *
   * @param deviceName
   * @param deviceType
   * @param customConfig - Optional custom configuration to use instead of generating default
   * @param customIpAddress - Optional custom IP address for default config generation
   * @param configOptions - Optional overrides for liveStreamEnable and simulatorEnabled
   * @returns if successful, returns response data from pairing API
   */
  const pairDevice = async (
    deviceName: string,
    deviceType: (typeof rcTypes)[number],
    customConfig?: any,
    customIpAddress?: string,
    configOptions?: { liveStreamEnable?: boolean; simulatorEnabled?: boolean },
  ): Promise<any> => {
    if (!deviceName?.trim()) {
      throw new Error('Device name is required')
    }

    if (!rcTypes.includes(deviceType)) {
      throw new Error('Invalid device type')
    }

    addDeviceToPairing(deviceName)

    try {
      let payload

      if (customConfig) {
        // Validate imported config against default structure
        const isValid = await validateImportedConfig(customConfig, deviceType)
        if (!isValid) {
          throw new Error('Invalid configuration: missing required fields from default config')
        }

        // Use custom config with generated pairing code
        payload = {
          config: {
            ...customConfig,
            deviceName, // Ensure device name matches
            organisationID: organisationId.value, // Ensure org ID is correct
          },
          code: generatePairingCode(),
        }
      } else {
        // Use default generated config with custom IP if provided
        payload = await generateDeviceConfig(deviceName, deviceType, customIpAddress, configOptions)
      }

      const response = await api.post(url, payload)

      // Store the pairing result with the code
      addPairingResult(deviceName, response.data.code)
      return response.data
    } catch (error) {
      log.error('Error pairing device:', error)
      throw error
    } finally {
      removeDeviceFromPairing(deviceName)
    }
  }
  /**
   * Dispatch the dock deploy workflow.
   *
   * FLY3D-612 — `correlation_id` is generated by the caller (must
   * happen BEFORE the WS subscribe so the same id is used in both
   * places). IFB-65 only forwards broadcasts to subscribers whose
   * correlation_id matches the deploy run, so the caller MUST have
   * already subscribed before this dispatch fires. Otherwise the
   * early events for the new run are emitted with no matching
   * subscriber and are dropped — the WS sees the subscribe ack and
   * then nothing.
   *
   * FLY3D-1085 — the workflow takes ONE input, `payload`, holding the
   * whole stringified contract object. The payload is validated against
   * `deploy-payload.schema.json` first, so a malformed object is caught
   * here with a readable message instead of as a 422 from GitHub (or,
   * worse, accepted and run against a dock).
   */
  const deployDock = async (
    payload: DeployDockPayload,
    correlationId: string,
  ): Promise<{ success: boolean; correlationId: string; error?: string }> => {
    const dockId = payload.dock_id ?? ''
    // `correlation_id` is stamped on here rather than taken from the
    // caller's object, so the dispatched trace id can never diverge
    // from the one the caller subscribed with.
    const contractPayload = { ...payload, correlation_id: correlationId }
    const schemaErrors = validateDeployPayload(contractPayload)
    if (schemaErrors.length) {
      const error = `Invalid deploy payload:\n${schemaErrors.map((e) => `  ${e}`).join('\n')}`
      log.error(error)
      updateDockDeploying(dockId, 'failed')
      return { success: false, correlationId, error }
    }
    // Backstop for the broker guard. Callers that hold the dock record
    // check it earlier (and cross-check the recorded host); this catches
    // every other path before a bad address reaches the dock.
    const mqttError = validateMqttHostForDock(payload.mqtt_host, { deviceID: dockId })
    if (mqttError) {
      log.error(mqttError)
      updateDockDeploying(dockId, 'failed')
      return { success: false, correlationId, error: mqttError }
    }
    try {
      // delete existing dock from docksDeploying if it exists
      docksDeploying.value = docksDeploying.value.filter((d) => d.dock_id !== dockId)
      docksDeploying.value.push({
        dock_id: dockId,
        status: 'deploying',
      })
      const url =
        'https://api.github.com/repos/Idroneimages-LTD/IDI-PROD-DEPLOY/actions/workflows/tailscale-deploy.yml/dispatches'
      const token = import.meta.env.VITE_GIT_TOKEN
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.github+json',
          'X-GitHub-Api-Version': '2022-11-28',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ref: 'main',
          // ONE input. The whole contract object is stringified into it.
          inputs: { payload: JSON.stringify(contractPayload) },
        }),
      })
      const success = response.status === 204
      if (!success) {
        const detail = await response.text().catch(() => '')
        log.error(`Deploy dispatch failed: ${response.status} ${detail}`)
        updateDockDeploying(dockId, 'failed')
        return {
          success,
          correlationId,
          error: `Deploy dispatch failed (${response.status}). ${detail}`.trim(),
        }
      }
      return { success, correlationId }
    } catch (error) {
      log.error('Error deploying dock:', error)
      updateDockDeploying(dockId, 'failed')
      return {
        success: false,
        correlationId,
        error: error instanceof Error ? error.message : 'Deploy dispatch failed',
      }
    }
  }
  const updateDockDeploying = (dock_id: string, status: 'deploying' | 'deployed' | 'failed') => {
    docksDeploying.value = docksDeploying.value.map((d) =>
      d.dock_id === dock_id ? { ...d, status } : d,
    )
  }

  /**
   * Fetches the latest workflow run ID for a specific dock with retry logic
   * @param dock_id - The dock ID to find the workflow run for
   * @param maxRetries - Maximum number of retries (default: 5)
   * @param retryDelay - Delay between retries in ms (default: 3000)
   * @returns Promise with the workflow run ID or null if not found
   */
  const getLatestWorkflowRunId = async (
    dock_id: string,
    maxRetries: number = 5,
    retryDelay: number = 3000,
  ): Promise<number | null> => {
    const token = import.meta.env.VITE_GIT_TOKEN

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const url =
          'https://api.github.com/repos/Idroneimages-LTD/IDI-PROD-DEPLOY/actions/workflows/tailscale-deploy.yml/runs?branch=main&per_page=10'
        const response = await fetch(url, {
          headers: {
            Accept: 'application/vnd.github.v3+json',
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })

        if (response.ok) {
          const data = await response.json()
          // Find the most recent run that has a job matching the dock_id
          for (const run of data.workflow_runs || []) {
            // Check if this run has jobs with the dock_id in the name
            const jobsUrl = run.jobs_url
            const jobsResponse = await fetch(jobsUrl, {
              headers: {
                Accept: 'application/vnd.github.v3+json',
                Authorization: `Bearer ${token}`,
              },
            })

            if (jobsResponse.ok) {
              const jobsData = await jobsResponse.json()
              const matchingJob = jobsData.jobs?.find((j: any) => j.name.includes(dock_id))
              if (matchingJob) {
                log.info(`Found workflow run ${run.id} for dock ${dock_id}`)
                return run.id
              }
            }
          }

          // If no matching job found and we have more retries, wait and try again
          if (attempt < maxRetries) {
            log.info(
              `No workflow run found for dock ${dock_id}, retrying in ${retryDelay}ms (attempt ${attempt}/${maxRetries})`,
            )
            await new Promise((resolve) => setTimeout(resolve, retryDelay))
          } else {
            log.warn(`No workflow run found for dock: ${dock_id} after ${maxRetries} attempts`)
          }
        } else {
          log.error('Error fetching workflow runs:', response.statusText)
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay))
          }
        }
      } catch (error) {
        log.error('Error getting latest workflow run ID:', error)
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
        }
      }
    }

    return null
  }

  /**
   * Maps GitHub job status to our DockDeploying status
   * @param jobStatus - GitHub job status (queued, in_progress, completed, etc.)
   * @param jobConclusion - GitHub job conclusion (success, failure, cancelled, etc.)
   * @returns DockDeploying status
   */
  const mapJobStatusToDeployStatus = (
    jobStatus: string,
    jobConclusion?: string,
  ): 'deploying' | 'deployed' | 'failed' => {
    if (jobStatus === 'completed') {
      if (jobConclusion === 'success') {
        return 'deployed'
      } else {
        return 'failed'
      }
    } else if (jobStatus === 'in_progress' || jobStatus === 'queued') {
      return 'deploying'
    } else {
      return 'failed'
    }
  }

  /**
   * Stops polling for a specific dock
   * @param dock_id - The dock ID to stop polling for
   */
  const stopPollDeployStatus = (dock_id: string) => {
    const interval = pollingIntervals.value.get(dock_id)
    if (interval) {
      clearInterval(interval)
      pollingIntervals.value.delete(dock_id)
      log.info(`Stopped polling for dock: ${dock_id}`)
    }
  }

  /**
   * Starts polling for deploy status and automatically stops when status is completed
   * @param dock_id - The dock ID to poll for
   * @param pollInterval - Polling interval in milliseconds (default: 10000)
   * @param maxDuration - Maximum polling duration in milliseconds (default: 10 minutes)
   * @returns The interval ID (can be used to manually stop polling)
   */
  const startPollDeployStatus = (
    dock_id: string,
    pollInterval: number = 10000,
    maxDuration: number = 600000,
  ) => {
    // Stop any existing polling for this dock
    stopPollDeployStatus(dock_id)

    const startTime = Date.now()
    let pollCount = 0

    const interval = setInterval(async () => {
      pollCount++
      const elapsedTime = Date.now() - startTime

      // Check if we've exceeded max polling duration
      if (elapsedTime >= maxDuration) {
        log.warn(
          `Polling timeout for dock ${dock_id} after ${Math.round(elapsedTime / 1000)}s and ${pollCount} polls`,
        )
        updateDockDeploying(dock_id, 'failed')
        stopPollDeployStatus(dock_id)
        return
      }

      const status = await checkDeployStatus(dock_id)

      // Stop polling if status is completed (deployed or failed)
      if (status === 'deployed' || status === 'failed') {
        stopPollDeployStatus(dock_id)
        log.info(
          `Polling stopped for dock ${dock_id} with status: ${status} after ${pollCount} polls`,
        )
      }
    }, pollInterval)

    pollingIntervals.value.set(dock_id, interval)
    log.info(`Started polling for dock: ${dock_id} (max duration: ${maxDuration / 1000}s)`)
    return interval
  }

  /**
   * Checks the deploy status for a specific dock
   * @param dock_id - The dock ID to check status for
   * @returns Promise with the deploy status ('deploying' | 'deployed' | 'failed')
   */
  const checkDeployStatus = async (
    dock_id: string,
  ): Promise<'deploying' | 'deployed' | 'failed'> => {
    try {
      const token = import.meta.env.VITE_GIT_TOKEN

      // Get the workflow run ID from storage, or fetch it if not available
      let workflowRunId = workflowRunIds.value.get(dock_id)

      if (!workflowRunId) {
        // Try to find the workflow run ID if not stored (single attempt, no retries here)
        const fetchedRunId = await getLatestWorkflowRunId(dock_id, 1, 0)
        if (fetchedRunId !== null) {
          workflowRunId = fetchedRunId
          workflowRunIds.value.set(dock_id, workflowRunId)
        } else {
          // Don't fail immediately - workflow might still be starting
          // Keep status as 'deploying' and let the next poll try again
          log.info(`Workflow run not ready yet for dock: ${dock_id}, will retry on next poll`)
          return 'deploying'
        }
      }

      const url = `https://api.github.com/repos/Idroneimages-LTD/IDI-PROD-DEPLOY/actions/runs/${workflowRunId}/jobs`
      const response = await fetch(url, {
        headers: {
          Accept: 'application/vnd.github.v3+json',
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      if (response.ok) {
        const data = await response.json()
        // get the job corresponding to dock id
        const job = data.jobs?.find((j: any) => j.name.includes(dock_id))
        if (job) {
          const status = mapJobStatusToDeployStatus(job.status, job.conclusion)
          updateDockDeploying(dock_id, status)
          log.info(`Deploy status for ${dock_id}:`, status)
          return status
        } else {
          // Job not found yet - workflow might still be initializing
          log.info(`Job not ready yet for dock: ${dock_id}, will retry on next poll`)
          return 'deploying'
        }
      } else {
        log.error('Error checking deploy status:', response.statusText)
        // Don't fail on API errors - could be transient
        return 'deploying'
      }
    } catch (error) {
      log.error('Error checking deploy status:', error)
      // Don't fail on errors - could be transient network issues
      return 'deploying'
    }
  }
  const pairButtonTexts = computed(() => {
    const texts: Record<string, string> = {}

    if (!organizationDevices.value || !Array.isArray(organizationDevices.value)) {
      return texts
    }

    for (const device of organizationDevices.value) {
      if (dockTypes.includes(device.deviceType)) {
        if (device.transferred_from && !device.onboarded) {
          texts[device.deviceID] = 'Onboard device'
          continue
        }
        // FLY3D-612 — prefer live WebSocket state over the legacy
        // GH-jobs polling. The WS feed is dock-type-agnostic and fed
        // by IFB-65 for every dock the org deploys.
        const live = deployEntries.value[device.deviceID]
        if (live) {
          if (live.status === 'failed') {
            texts[device.deviceID] = 'Deploy failed'
          } else if (live.status === 'success') {
            texts[device.deviceID] = 'Deployed'
          } else if (
            live.status === 'live' ||
            live.status === 'connecting'
          ) {
            texts[device.deviceID] = `Deploying (${live.currentStage}/5)`
          } else if (live.status === 'error') {
            texts[device.deviceID] = 'Connection lost'
          } else {
            texts[device.deviceID] = 'Deploy Dock'
          }
          continue
        }
        // Fallback to the legacy GH-jobs flag for any device that
        // started under the old polling path.
        const deploy = docksDeploying.value.find((d) => d.dock_id === device.deviceID)
        texts[device.deviceID] = deploy?.status || 'Deploy Dock'
      } else if (isDevicePairing(device.deviceID)) {
        texts[device.deviceID] = 'Pairing...'
      } else if (isDeviceCopied(device.deviceID)) {
        texts[device.deviceID] = 'Copied!'
      } else {
        const code = getPairingCodeForDevice(device.deviceID)
        texts[device.deviceID] = code || 'Pair Device'
      }
    }

    return texts
  })
  return {
    generatePairingCode,
    generateDeviceConfig,
    getSimlocation,
    pairDevice,
    validateImportedConfig,
    devicesInPairing,
    addDeviceToPairing,
    removeDeviceFromPairing,
    isDevicePairing,
    pairingResults,
    addPairingResult,
    clearPairingResults,
    getPairingCodeForDevice,
    isDeviceCopied,
    copyPairingCodeForDevice,
    deployDock,
    startPollDeployStatus,
    stopPollDeployStatus,
    checkDeployStatus,
    docksDeploying,
    updateDockDeploying,
    getLatestWorkflowRunId,
    workflowRunIds,
    organizationDevices,
    pairButtonTexts,
  }
}
